import React, { useState, useEffect, useRef } from 'react'
import { useOutletContext, useNavigate } from 'react-router-dom'
import { DataTable, Button } from '../../../components'
import { useMessage, useModalDialog } from '../../../hooks'
import { companyService } from '../../../services'
import { useNetworkDialog } from '../hooks/useNetworkDialog'

const flatten = (c) => ({
  ...c,
  clientPills: c.is_client ? [{ label: 'Kund', className: 'pill pill-green' }] : [],
})

// See PersonList.js's BulkActionsMenu comment for why "Redigera"/"Ta bort"
// stay off the toolbar (DataTableToolbar's bulk buttons call onClick() with
// no arguments) and genuine bulk-only actions live in this dropdown instead.
const BulkActionsMenu = ({ items, onExport, onDelete }) => {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef()

  useEffect(() => {
    const onDocClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

  const run = (fn) => { setOpen(false); fn(items) }

  return (
    <div className="network-actions-menu-wrap" ref={wrapRef}>
      <Button value={`Åtgärder (${items.length}) ▾`} className="main-toolbar-item button" onClick={() => setOpen(o => !o)} />
      {open &&
        <div className="network-actions-menu">
          <button type="button" onClick={() => run(onExport)}>Exportera (CSV)</button>
          <button type="button" className="network-actions-menu-danger" onClick={() => run(onDelete)}>Ta bort</button>
        </div>
      }
    </div>
  )
}

const CompanyList = () => {

  const appName = useOutletContext()
  const navigate = useNavigate()
  const { showMessage } = useMessage()
  const { hideModalDialog } = useModalDialog()
  const { showCompanyDialog, showDeleteCompanyDialog } = useNetworkDialog()

  const [companies, setCompanies] = useState([])
  const [selectedItems, setSelectedItems] = useState([])
  const [loading, setLoading] = useState(true)

  const fetchCompanies = () => {
    setLoading(true)
    companyService.getAll()
      .then(res => { setCompanies((res.companies || []).map(flatten)); setLoading(false) })
      .catch(() => { showMessage('Kunde inte ladda företag', 'error'); setLoading(false) })
  }

  useEffect(() => { fetchCompanies() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const deleteCompanies = (items) => {
    showDeleteCompanyDialog(items, async (list) => {
      await Promise.all(list.map(c => companyService.destroy(c.company_uuid)))
        .then(() => { hideModalDialog(); showMessage(list.length > 1 ? 'Borttagna' : 'Borttaget'); setSelectedItems([]); fetchCompanies() })
        .catch(err => showMessage(err.message || 'Kunde inte ta bort', 'error'))
    })
  }

  // Client-side only, same pattern as recruitment's CandidateList export.
  const exportSelected = (items) => {
    const headers = ['Namn', 'Bransch', 'Webbplats', 'Antal personer', 'Kund']
    const escape = (value) => {
      const s = value == null ? '' : String(value)
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
    }
    const rows = items.map(c => [
      c.company_name,
      c.company_industry,
      c.company_website,
      c.person_count,
      c.is_client ? 'Ja' : 'Nej',
    ].map(escape).join(','))

    const csv = '﻿' + [headers.join(','), ...rows].join('\r\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `foretag_${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <div className="wide-margin">
      <DataTable
        data={companies}
        idColumn="company_uuid"
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        loading={loading}
        filterable
        filterObjects={['company_name', 'company_industry']}
        columns={[
          { id: 'company_uuid', name: 'Visa', type: 'button', label: 'Visa', data: { data: 'company_uuid' },
            onClick: (uuid) => navigate(`/${appName}/companies/${uuid}`) },
          { id: 'company_name', name: 'Namn', className: 'bold' },
          { id: 'company_industry', name: 'Bransch' },
          { id: 'company_website', name: 'Webbplats' },
          { id: 'person_count', name: 'Antal personer' },
          { id: 'clientPills', name: 'Kund', type: 'pills' },
        ]}
        buttons={[
          { label: '+ Nytt företag', action: 'add', mainButton: true, onClick: () => showCompanyDialog(null, () => fetchCompanies()) },
          { label: 'Redigera', action: 'edit', hideInToolbar: true, onClick: (items) => showCompanyDialog(items[0], () => fetchCompanies()) },
          { customButton: selectedItems.length > 0
              ? <BulkActionsMenu items={selectedItems} onExport={exportSelected} onDelete={deleteCompanies} />
              : null },
          { label: 'Ta bort', action: 'delete', hideInToolbar: true, onClick: (items) => deleteCompanies(items || selectedItems) },
        ]}
      />
    </div>
  )
}

export default CompanyList
